// components/common/RelatedItems.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, ExternalLink, Plus } from 'lucide-react';

/**
 * Reusable component to display related items/records
 * 
 * @param {string} title - Section title (e.g., "Related Orders", "Payment History")
 * @param {string} apiEndpoint - API endpoint to fetch data (optional if data is provided)
 * @param {array} data - Static data array (optional if apiEndpoint is provided)
 * @param {array} columns - Array of column definitions
 * @param {function} onRowClick - Callback when row is clicked
 * @param {boolean} collapsible - Whether section can be collapsed
 * @param {boolean} defaultExpanded - Initial expanded state
 * @param {string} emptyMessage - Message when no data
 * @param {boolean} showAddButton - Show add new button
 * @param {function} onAdd - Callback for add button
 * @param {string} addButtonLabel - Label for add button
 */
const RelatedItems = ({
  title,
  apiEndpoint,
  data: staticData,
  columns = [],
  onRowClick,
  collapsible = true,
  defaultExpanded = true,
  emptyMessage = "No items found",
  showAddButton = false,
  onAdd,
  addButtonLabel = "Add New",
  token,
  formatData,
  loading: externalLoading = false
}) => {
  const [data, setData] = useState(staticData || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(defaultExpanded);



  // Update data when staticData changes
  useEffect(() => {
    if (staticData) {
      setData(staticData);
    }
  }, [staticData]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = token ? { "x-auth-token": token } : {};
      const response = await axios.get(apiEndpoint, { headers });
      
      let fetchedData = response.data;
      
      // Handle nested data response
      if (fetchedData.data) {
        fetchedData = fetchedData.data;
      }
      
      // Apply custom formatting if provided
      if (formatData) {
        fetchedData = formatData(fetchedData);
      }
      
      setData(fetchedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching related items:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  },[]);

    // Fetch data if apiEndpoint is provided
  useEffect(() => {
    if (apiEndpoint && !staticData) {
      fetchData();
    }
  }, [apiEndpoint,staticData, fetchData]);


  const renderCellValue = (column, row) => {
    const value = column.key ? row[column.key] : null;
    
    // Use custom render function if provided
    if (column.render) {
      return column.render(value, row);
    }
    
    // Default rendering based on type
    if (column.type === 'date' && value) {
      return new Date(value).toLocaleDateString();
    }
    
    if (column.type === 'currency' && value !== null && value !== undefined) {
      return `$${parseFloat(value).toFixed(2)}`;
    }
    
    if (column.type === 'badge') {
      const colorMap = column.colorMap || {};
      const color = colorMap[value] || 'bg-gray-100 text-gray-800';
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
          {value}
        </span>
      );
    }
    
    return value || '-';
  };

  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const isLoading = loading || externalLoading;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            {!isLoading && data.length > 0 && (
              <span className="px-2 py-1 bg-gray-300 text-gray-800 rounded-full text-xs font-bold">
                {data.length}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {showAddButton && onAdd && (
              <button
                onClick={onAdd}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                {addButtonLabel}
              </button>
            )}
            
            {collapsible && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                {expanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">{error}</p>
              {apiEndpoint && (
                <button
                  onClick={fetchData}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Retry
                </button>
              )}
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">{emptyMessage}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        className={`py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider ${
                          column.align === 'right' ? 'text-right' :
                          column.align === 'center' ? 'text-center' :
                          'text-left'
                        }`}
                      >
                        {column.label}
                      </th>
                    ))}
                    {onRowClick && (
                      <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, rowIndex) => (
                    <tr
                      key={row.uuid || row.id || rowIndex}
                      className={`border-b border-gray-100 ${
                        onRowClick ? 'hover:bg-gray-50 cursor-pointer' : ''
                      }`}
                      onClick={() => !onRowClick && handleRowClick(row)}
                    >
                      {columns.map((column, colIndex) => (
                        <td
                          key={colIndex}
                          className={`py-3 px-4 text-sm text-gray-900 ${
                            column.align === 'right' ? 'text-right' :
                            column.align === 'center' ? 'text-center' :
                            'text-left'
                          }`}
                        >
                          {renderCellValue(column, row)}
                        </td>
                      ))}
                      {onRowClick && (
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(row);
                            }}
                            className="text-blue-600 hover:text-blue-700 flex items-center gap-1 ml-auto"
                          >
                            <span className="text-sm">View</span>
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RelatedItems;